import * as React from 'react';
import { getDoc, setDoc, getDocs, query, doc, collection, Timestamp, where, orderBy } from "firebase/firestore";
import { database } from '../firebase-config';
import { roundToDollars } from '../utils/MoneyUtils';
import { History } from '../data/History';
import { getPackagingMaps } from "./PackagingData";
import { getRecipeMapFromDB } from "./RecipeData";

export const COLLECTION = "products";

export class Product {
  document_reference = ""
  name = ""
  description = ""
  category = ""
  cost_high = 0.0
  cost_low = 0.0
  packaging = new Array()
  packaging_cost = 0.0
  ingredient_label_file = ""
  name_label_file = ""
  google_image_file_id = ""
  on_website = false
  price = ""
  recipe = ""
  recipe_qty = 0
  is_deleted = false
  last_update = ""
  history = new Array()
}
export class ProductMaps {
  productMap = new Map()
  productMapNoDeleted = new Map()
}

export class Products {
  options = new Array()
  docMap = new Map()
  refMap = new Map()
}

export const getProductMaps = async () => {
  let productMaps = new ProductMaps();

  try {
    const docQuery = query(collection(database, COLLECTION));
    const querySnapshot = await getDocs(docQuery);
    querySnapshot.forEach((doc) => {
      productMaps.productMap.set(doc.id, doc.data());
      if (!doc.data().is_deleted) {
        productMaps.productMapNoDeleted.set(doc.id, doc.data());
      }

    });

  } catch (e) {
    console.log("Error getting products:", e);
  }
  return productMaps;
}

export const getProducts = async (category, get_deleted) => {
  let products = new Products();

  try {

    let docQuery = '';
    if (category && category != "All") {
      docQuery = query(collection(database, COLLECTION),
        where('category', '==', category),
        orderBy("name", "asc"));
    } else {
      docQuery = query(collection(database, COLLECTION), orderBy("name", "asc"));
    }

    const querySnapshot = await getDocs(docQuery);
    querySnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      if ((get_deleted)
        || (!get_deleted && !doc.data().is_deleted)) {
        products.docMap.set(doc.id, doc.data());
        products.refMap.set(doc.id, React.createRef());
        let option = { id: doc.id, value: doc.data().name };
        products.options.push(option);
      }
    });

  } catch (e) {
    console.log("Error getting cached document:", e);
  }
  return products;
}

export const getProduct = async (documentRef) => {
  try {
    const existingDocRef = doc(database, COLLECTION, documentRef);
    const existingDoc = await getDoc(existingDocRef);
    return existingDoc;
  } catch (e) {
    console.log("getProduct(): Error getting Product ref:" + this.props.docRef);
  }
}

export function getDocData(product) {

  let now = Timestamp.now();

  let ingredient_label_file = "";
  if (product.ingredient_label_file) {
    ingredient_label_file = product.ingredient_label_file;
  }
  let name_label_file = "";
  if (product.name_label_file) {
    name_label_file = product.name_label_file;
  }

  let google_image_file_id = "";
  if (product.google_image_file_id) {
    google_image_file_id = product.google_image_file_id;
  }

  let description = "";
  if (product.description) {
    description = product.description;
  }

  let docData = {
    name: product.name,
    description: description,
    category: product.category,
    packaging: product.packaging,
    cost_high: product.cost_high,
    cost_low: product.cost_low,
    ingredient_label_file: ingredient_label_file,
    name_label_file: name_label_file,
    google_image_file_id: google_image_file_id,
    packaging_cost: product.packaging_cost,
    price: product.price,
    recipe: product.recipe,
    recipe_qty: product.recipe_qty,
    on_website: product.on_website,
    is_deleted: product.is_deleted,
    last_update: now,
    history: product.history,
  };

  return docData;

}

export function documentToProduct(existingDoc) {
  let existingProduct = new Product();
  existingProduct.document_reference = existingDoc.id;
  existingProduct.name = existingDoc.data().name;
  existingProduct.cost_high = existingDoc.data().cost_high;
  existingProduct.cost_low = existingDoc.data().cost_low;
  existingProduct.description = existingDoc.data().description;
  existingProduct.category = existingDoc.data().category;
  existingProduct.packaging = existingDoc.data().packaging;
  existingProduct.packaging_cost = existingDoc.data().packaging_cost;
  existingProduct.price = existingDoc.data().price;
  existingProduct.recipe = existingDoc.data().recipe;
  existingProduct.recipe_qty = existingDoc.data().recipe_qty;
  existingProduct.ingredient_label_file = existingDoc.data().ingredient_label_file;
  existingProduct.name_label_file = existingDoc.data().name_label_file;
  existingProduct.google_image_file_id = existingDoc.data().google_image_file_id;
  existingProduct.on_website = (existingDoc.data().on_website ? existingDoc.data().on_website : false);
  existingProduct.is_deleted = (existingDoc.data().is_deleted ? existingDoc.data().is_deleted : false);
  existingProduct.last_update = existingDoc.data().last_update;
  existingProduct.history = existingDoc.data().history;

  return existingProduct;
}

export const productNameExists = async (documentRef) => {
  let existingDoc = await getProduct(documentRef);
  if (existingDoc && existingDoc.data() && existingDoc.id === documentRef) {
    return true;
  } else {
    return false;
  }
}

export const saveProduct = async (docRef, productData) => {
  try {
    await setDoc(doc(database, COLLECTION, docRef), productData, { merge: true });
  } catch (e) {
    console.log("Error saving Product:", e);
  }
}

export function recalculateCosts(product, recipeMap, packagingMap) {
  let tempProduct = product;

  let totalPackagingCosts = getTotalPackagingCosts(product, packagingMap);
  let recipeCostHigh = getRecipeCostHigh(product, recipeMap);
  let recipeCostLow = getRecipeCostLow(product, recipeMap);
  let totalCostHigh = totalPackagingCosts + (recipeCostHigh * product.recipe_qty);
  let totalCostLow = totalPackagingCosts + (recipeCostLow * product.recipe_qty);

  tempProduct.cost_high = totalCostHigh;
  tempProduct.cost_low = totalCostLow;
  tempProduct.packaging_cost = totalPackagingCosts;

  return tempProduct;

}

export function getRecipeCostHigh(product, recipeMap) {
  if (!product.recipe || product.recipe === '') {
    console.log('No recipe selected to calculate recipe cost!');
    return 0.0;
  }
  let recipeDoc = recipeMap.get(product.recipe);
  if (recipeDoc) {
    return recipeDoc.cost_per_item_high;
  } else {
    console.log('recipe ref:' + product.recipe + ' not found in recipeMap!');
    return 0.0;
  }
}

export function getRecipeCostLow(product, recipeMap) {
  if (!product.recipe || product.recipe === '') {
    console.log('No recipe selected to calculate recipe cost!');
    return 0.0;
  }
  let recipeDoc = recipeMap.get(product.recipe);
  if (recipeDoc) {
    return recipeDoc.cost_per_item_low;
  } else {
    console.log('recipe ref:' + product.recipe + ' not found in recipeMap!');
    return 0.0;
  }
}

export function getTotalPackagingCosts(product, packagingMap) {
  let packagingTotal = 0.0;
  if (!product.packaging) {
    return packagingTotal;
  }
  product.packaging.forEach((packagingRefQuantity) => {
    let packagingParts = packagingRefQuantity.split(":");
    let packagingRef = packagingParts[0];
    let quantity = packagingParts[1];
    let packagingDoc = packagingMap.get(packagingRef);
    packagingTotal = packagingTotal + (packagingDoc.cost * quantity);
  })
  return packagingTotal;
}

export async function updateAffectedProductsRecipe(recipeRef) {
  /**
   * Get the list of impacted recipies.  Since the ingredient cost can change, the cost
   * of the recipe will change.
   */
  let affectedProducts = await getAffectedProductsRecipe(recipeRef);
  /**
   * For each affected recipe:
   * - recalculate the costs
   * - save the recipe
   * - update the history
   */
  affectedProducts.forEach(async (affectedProduct) => {
    let productRef = affectedProduct.id;
    let productData = affectedProduct.data();
    let recipeMaps = await getRecipeMapFromDB();
    let recipeMap = recipeMaps.recipeMap;
    let packagingMap = await getPackagingMaps();
    let updatedProduct = recalculateCosts(productData, recipeMap, packagingMap);
    let now = Timestamp.now();
    await recordHistory(updatedProduct.history, now, updatedProduct, productRef);
    saveProduct(productRef, updatedProduct);
  });
}

export async function updateAffectedProductsPackaging(packagingRef) {
  /**
   * Get the list of impacted recipies.  Since the ingredient cost can change, the cost
   * of the recipe will change.
   */
  let affectedProducts = await getAffectedProductsPackaging(packagingRef);
  /**
   * For each affected recipe:
   * - recalculate the costs
   * - save the recipe
   * - update the history
   */
  affectedProducts.forEach(async (affectedProduct) => {
    let productRef = affectedProduct.id;
    let productData = affectedProduct.data();
    let recipeMaps = await getRecipeMapFromDB();
    let recipeMap = recipeMaps.recipeMap;
    let packagingMap = await getPackagingMaps();
    let updatedProduct = recalculateCosts(productData, recipeMap, packagingMap);
    let now = Timestamp.now();
    await recordHistory(updatedProduct.history, now, updatedProduct, productRef);
    saveProduct(productRef, updatedProduct);
  });
}
async function getAffectedProductsRecipe(recipeRef) {
  let affectedProducts = new Array();
  try {
    const docQuery = query(collection(database, COLLECTION));
    const querySnapshot = await getDocs(docQuery);
    let queryData = querySnapshot.docs;
    querySnapshot.forEach((product) => {
      if (product.data().recipe === recipeRef) {
        affectedProducts.push(product);
      }
    });

    return affectedProducts;
  } catch (e) {
    console.log("Error determining affected Products:", e);
  }
}

async function getAffectedProductsPackaging(packagingRef) {
  let affectedProducts = new Array();
  try {

    const docQuery = query(collection(database, COLLECTION));

    const querySnapshot = await getDocs(docQuery);
    querySnapshot.forEach((product) => {
      let packagingList = new Array();
      packagingList = product.data().packaging;
      packagingList.forEach((packaging) => {
        if (packaging.startsWith(packagingRef)) {
          affectedProducts.push(product);
        }
      });
    });

    return affectedProducts;
  } catch (e) {
    console.log("Error determining affected Products:", e);
  }
}

/**
* ------------ Start History ----------------
*/
export const getHistoryRecord = async (history_key) => {
  const existingHistoryRef = doc(database, "history", history_key);
  const existingHistory = await getDoc(existingHistoryRef);
  const historyRecord = new History();
  historyRecord.key = history_key;
  historyRecord.updateDate = existingHistory.data().last_updated;
  historyRecord.field = existingHistory.data().field;
  historyRecord.oldValue = existingHistory.data().oldValue;
  historyRecord.newValue = existingHistory.data().newValue;
  return historyRecord;
}

export const loadHistoryMap = async (historyList) => {
  let map = new Map();
  let reversedHistory = historyList.reverse();
  for (let ii = 0; ii < 21 && ii < historyList.length; ii++) {
    try {
      let historyRecord = await getHistoryRecord(reversedHistory[ii]);
      map.set(reversedHistory[ii], historyRecord);
    } catch (e) {
      console.log("Error getting history ref:" + reversedHistory[ii]);
    }
  }
  return map;
}

function addHistory(newHistoryList, now, productName, field, oldValue, newValue) {
  let docData = {
    last_updated: now,
    field: field,
    oldValue: oldValue,
    newValue: newValue,
  };

  let documentName = 'product_' + productName + '_' + field + "_" + now;

  try {
    setDoc(doc(database, "history", documentName), docData);
  } catch (e) {
    console.log("Error saving History:", e);
    return;
  }

  newHistoryList.push(documentName);
}

function packagingEqual(newPackaging, oldPackaging) {
  if (newPackaging.length !== oldPackaging.length) return false;
  for (let ii = 0; ii < newPackaging.length; ii++) {
    if (newPackaging[ii] !== oldPackaging[ii]) return false;
  }
  return true;
}

export const recordHistory = async (newHistoryList, now, newProduct, docRef) => {
  let existingDoc = "";
  try {
    let oldDocRef = doc(database, COLLECTION, docRef);
    existingDoc = await getDoc(oldDocRef);
  } catch (e) {
    console.log("Error getting existing Product for History:", e);
  }

  try {
    let oldProduct = existingDoc.data();
    if (newProduct.category !== oldProduct.category) {
      addHistory(newHistoryList, now, docRef, "category", oldProduct.category, newProduct.category);
    }
    if (newProduct.description !== oldProduct.description) {
      addHistory(newHistoryList, now, docRef, "description", oldProduct.description, newProduct.description);
    }
    if (newProduct.google_image_file_id !== oldProduct.google_image_file_id) {
      addHistory(newHistoryList, now, docRef, "google_image_file_id", oldProduct.google_image_file_id, newProduct.google_image_file_id);
    }
    if (newProduct.ingredient_label_file !== oldProduct.ingredient_label_file) {
      addHistory(newHistoryList, now, docRef, "ingredient_label_file", oldProduct.ingredient_label_file, newProduct.ingredient_label_file);
    }
    if (oldProduct.name && newProduct.name !== oldProduct.name) {
      addHistory(newHistoryList, now, docRef, "name", oldProduct.name, newProduct.name);
    }
    if (newProduct.name_label_file !== oldProduct.name_label_file) {
      addHistory(newHistoryList, now, docRef, "name_label_file", oldProduct.name_label_file, newProduct.name_label_file);
    }
    if (newProduct.on_website !== oldProduct.on_website) {
      addHistory(newHistoryList, now, docRef, "on_website", oldProduct.on_website, newProduct.on_website);
    }
    if (newProduct.on_website !== oldProduct.on_website) {
      addHistory(newHistoryList, now, docRef, "on_website", oldProduct.on_website, newProduct.on_website);
    }
    if (newProduct.packaging_cost !== oldProduct.packaging_cost) {
      addHistory(newHistoryList, now, docRef, "packaging_cost", roundToDollars(oldProduct.packaging_cost), roundToDollars(newProduct.packaging_cost));
    }
    if (newProduct.cost_high !== oldProduct.cost_high) {
      addHistory(newHistoryList, now, docRef, "cost_high", roundToDollars(oldProduct.cost_high), roundToDollars(newProduct.cost_high));
    }
    if (newProduct.cost_low !== oldProduct.cost_low) {
      addHistory(newHistoryList, now, docRef, "cost_low", roundToDollars(oldProduct.cost_low), roundToDollars(newProduct.cost_low));
    }
    if (newProduct.price !== oldProduct.price) {
      addHistory(newHistoryList, now, docRef, "price", roundToDollars(oldProduct.price), roundToDollars(newProduct.price));
    }
    if (newProduct.recipe !== oldProduct.recipe) {
      addHistory(newHistoryList, now, docRef, "recipe", oldProduct.recipe, newProduct.recipe);
    }
    if (newProduct.recipe_qty !== oldProduct.recipe_qty) {
      addHistory(newHistoryList, now, docRef, "recipe_qty", oldProduct.recipe_qty, newProduct.recipe_qty);
    }
    if (newProduct.is_deleted !== oldProduct.is_deleted) {
      let oldProductDeleted = "";
      if (oldProduct.is_deleted === undefined) {
        oldProductDeleted = "undefined"
      } else if (oldProduct.is_deleted === true) {
        oldProductDeleted = "True"
      } else if (oldProduct.is_deleted === false) {
        oldProductDeleted = "False"
      }

      let newProductDeleted = "";
      if (newProduct.is_deleted === undefined) {
        newProductDeleted = "undefined"
      } else if (newProduct.is_deleted === true) {
        newProductDeleted = "True"
      } else if (newProduct.is_deleted === false) {
        newProductDeleted = "False"
      }
      addHistory(newHistoryList, now, docRef, "is_deleted", oldProductDeleted, newProductDeleted);
    }
    if (!packagingEqual(newProduct.packaging, oldProduct.packaging)) {
      addHistory(newHistoryList, now, docRef, "packaging", oldProduct.packaging, newProduct.packaging);
    }
  } catch (e) {
    console.log("Error adding history for:" + existingDoc.id);
  }
}
/**
 * ------------ End History ----------------
 */
